<script setup lang="ts">
import {computed, onBeforeUnmount, watch} from 'vue'
import {useRouter} from 'vue-router'
import {useI18n} from 'vue-i18n'
import {useNotificationStore} from '@toolify/client/src/stores/NotificationStore/useNotificationStore'
import {mapNotificationToItem} from '@toolify/client/src/stores/NotificationStore/singletons/mapNotificationToItem'
import FloatingNotificationItemComponent from '@toolify/client/src/modules/notifications/components/FloatingNotificationItemComponent.vue'
import FloatingMessageStackComponent from '@toolify/client/src/modules/notifications/components/FloatingMessageStackComponent.vue'
import {useNotificationApiStore} from '@toolify/client/src/stores/api/NotificationApiStore/useNotificationApiStore'
import {useResponsive} from '@toolify/client/src/composables/useResponsive'
import {getEntityNameByEntity} from '@toolify/client/src/modules/entities/functions/getEntityNameByEntity'
import UserAvatarComponent from '@toolify/client/src/modules/entities/user/UserAvatarComponent.vue'
import IconComponent from '@toolify/client/src/modules/ui/components/IconComponent.vue'
import {IconNameType} from '@toolify/server/src/enum/IconNameType'
import {SizeVariantType} from '@toolify/client/src/modules/ui/enum/SizeVariantType'
import {INotificationItem} from '@toolify/client/src/modules/notifications/types/INotificationItem'
import {MessageSeverityType} from '@toolify/client/src/modules/notifications/enum/MessageSeverityType'
import {IFloatingStackLayoutEntry} from '@toolify/client/src/modules/notifications/functions/computeFloatingStackLayout'
import {useLocalMessageStore} from '@toolify/client/src/stores/LocalMessageStore/useLocalMessageStore'
import {ILocalMessageInteraction} from '@toolify/client/src/stores/LocalMessageStore/types/ILocalMessageInteraction'
import {useAuthStore} from '@toolify/client/src/stores/AuthStore/useAuthStore'

interface Props {
  // off in the Windows notification window mode and with HideFloatingNotifications; local messages always show
  showServerPopups?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  showServerPopups: true,
})

interface IServerStackEntry {
  id: string
  notificationItem: INotificationItem
}

const SERVER_KEY_PREFIX = 'server:'

const router = useRouter()
const {t} = useI18n()
const notificationApiStore = useNotificationApiStore()
const localMessageStore = useLocalMessageStore()
const authStore = useAuthStore()
const {isSmallerThanLarge} = useResponsive()
const notificationStore = useNotificationStore()

// keyed by the stack entry key (server:<id>)
const serverInteractions = new Map<string, ILocalMessageInteraction>()
let collapsedServerIds = new Set<string>()

const notificationItems = computed(() => {
  return notificationStore.popupNotifications.map(notification => {
    return {
      notification,
      notificationItem: mapNotificationToItem(notification),
    }
  }).filter(item => item.notificationItem)
})

const visibleNotificationItems = computed(() => {
  return props.showServerPopups ? notificationItems.value : []
})

// below 1024 px server popups keep today's top pill; the shared stack holds them only on desktop
const serverStackItems = computed(() => {
  return isSmallerThanLarge.value ? [] : visibleNotificationItems.value
})

const serverStackEntriesByKey = computed(() => {
  return new Map<string, IServerStackEntry>(serverStackItems.value.map(({notification, notificationItem}) => {
    return [getServerKey(notification.id), {
      id: notification.id,
      notificationItem,
    }]
  }))
})

const latestItem = computed(() => {
  const items = visibleNotificationItems.value
  return items.length ? items[items.length - 1] : null
})

const pillAuthorName = computed(() => {
  const entity = latestItem.value?.notificationItem.authorEntity
  return entity ? getEntityNameByEntity(entity) : ''
})

const serverLayoutSignature = computed(() => {
  return serverStackItems.value.map(({notification}) => {
    return `${notification.id}:${notificationStore.getPopupNotificationQueuedAt(notification.id) ?? 0}`
  }).join('|')
})

function getServerKey(id: string): string {
  return `${SERVER_KEY_PREFIX}${id}`
}

function getServerStackEntry(key: string): IServerStackEntry | undefined {
  return serverStackEntriesByKey.value.get(key)
}

function getServerInteraction(key: string): ILocalMessageInteraction {
  let interaction = serverInteractions.get(key)
  if(!interaction) {
    interaction = {
      hovered: false,
      focused: false,
      touched: false,
    }
    serverInteractions.set(key, interaction)
  }
  return interaction
}

// read by the stack on every layout, so the hover / focus / touch state is always the current one
function getServerLayoutEntries(): IFloatingStackLayoutEntry[] {
  const keys = new Set<string>()
  const entries = serverStackItems.value.map<IFloatingStackLayoutEntry>(({notification}, index) => {
    const key = getServerKey(notification.id)
    const interaction = getServerInteraction(key)
    keys.add(key)
    return {
      key,
      queuedAt: notificationStore.getPopupNotificationQueuedAt(notification.id) ?? 0,
      seq: index,
      isPinned: false,
      pinRank: 1,
      severity: MessageSeverityType.Neutral,
      isHovered: interaction.hovered,
      isFocused: interaction.focused,
      isTouched: interaction.touched,
    }
  })
  serverInteractions.forEach((_, key) => {
    if(!keys.has(key)) {
      serverInteractions.delete(key)
    }
  })
  return entries
}

function syncServerCollapsed(nextCollapsedIds: Set<string>) {
  collapsedServerIds.forEach(id => {
    if(!nextCollapsedIds.has(id)) {
      notificationStore.setPopupNotificationCollapsed(id, false)
    }
  })
  nextCollapsedIds.forEach(id => {
    if(!collapsedServerIds.has(id)) {
      notificationStore.setPopupNotificationCollapsed(id, true)
    }
  })
  collapsedServerIds = nextCollapsedIds
}

function onServerLayout(collapsedKeys: string[]) {
  syncServerCollapsed(new Set(collapsedKeys.map(key => key.slice(SERVER_KEY_PREFIX.length))))
}

// the pill's "Close all" also closes the popups in the stack, visible or folded
function dismissServerStackEntries() {
  serverStackItems.value.forEach(({notification}) => {
    notificationStore.dismissPopupNotification(notification.id)
  })
}

function dismissAllPopups() {
  for (const notification of [...notificationStore.popupNotifications]) {
    notificationStore.dismissPopupNotification(notification.id)
  }
}

function openLatest() {
  const latest = latestItem.value
  dismissAllPopups()
  if (!latest) {
    return
  }
  const to = latest.notificationItem.to ?? latest.notificationItem.detailsTo
  if (to) {
    router.push(to)
  }
}

async function openServerEntry(key: string) {
  const entry = getServerStackEntry(key)
  if(!entry) {
    return
  }
  notificationStore.dismissPopupNotification(entry.id)
  const to = entry.notificationItem.to ?? entry.notificationItem.detailsTo
  if (to) {
    await router.push(to)
  }
}

function dismissServerEntry(key: string) {
  const entry = getServerStackEntry(key)
  if(entry) {
    notificationStore.dismissPopupNotification(entry.id)
  }
}

async function markAsRead(notificationId: string, isAggregationGroup?: boolean) {
  if (isAggregationGroup) {
    await notificationApiStore.markNotificationGroupAsRead(notificationId)
    return
  }

  await notificationApiStore.markNotificationAsRead(notificationId)
}

async function markAsUnread(notificationId: string) {
  await notificationApiStore.markNotificationAsUnread(notificationId)
}

function setServerHovered(key: string, isHovered: boolean) {
  const entry = getServerStackEntry(key)
  getServerInteraction(key).hovered = isHovered
  if(entry) {
    notificationStore.setPopupNotificationHovered(entry.id, isHovered)
  }
}

function onServerFocusOut(key: string, event: FocusEvent) {
  const wrapper = event.currentTarget instanceof HTMLElement ? event.currentTarget : null
  const nextTarget = event.relatedTarget instanceof Node ? event.relatedTarget : null
  if(!wrapper?.contains(nextTarget)) {
    getServerInteraction(key).focused = false
  }
}

function onServerPointerDown(key: string, event: PointerEvent) {
  if(event.pointerType === 'touch') {
    getServerInteraction(key).touched = true
  }
}

watch(() => authStore.currentEntity?.entityId, (entityId, previousEntityId) => {
  if(previousEntityId && entityId !== previousEntityId) {
    localMessageStore.clear()
  }
})

onBeforeUnmount(() => {
  syncServerCollapsed(new Set())
})
</script>

<template>
  <FloatingMessageStackComponent
    class="floating-notifications-overlay"
    :getExternalEntries="getServerLayoutEntries"
    :externalRevision="serverLayoutSignature"
    @externalLayout="onServerLayout"
    @closeAll="dismissServerStackEntries"
  >
    <div
      v-if="isSmallerThanLarge && visibleNotificationItems.length"
      class="notification-pill"
    >
      <button
        type="button"
        class="pill-main"
        @click="openLatest"
      >
        <span class="pill-avatar" v-if="latestItem">
          <UserAvatarComponent
            :entity="latestItem.notificationItem.authorEntity"
            :alt="t('notifications.floatingNotifications.avatarAlt')"
          />
        </span>
        <span class="pill-label">{{pillAuthorName}}</span>
        <span
          v-if="visibleNotificationItems.length > 1"
          class="tw-bg-red-600 tw-text-on-primary tw-rounded-full tw-text-2xs tw-min-w-4 tw-h-4 tw-px-1 tw-flex tw-justify-center tw-items-center tw-font-medium"
        >
          {{visibleNotificationItems.length}}
        </span>
      </button>
      <button
        type="button"
        class="pill-close"
        :aria-label="t('notifications.floatingNotifications.dismissAll')"
        @click.stop="dismissAllPopups"
      >
        <IconComponent :name="IconNameType.Xmark" :size="SizeVariantType.XSmall"/>
      </button>
    </div>

    <template #externalEntry="{entryKey, isCollapsed}">
      <div
        v-if="getServerStackEntry(entryKey)"
        class="stack-entry"
        data-kind="server"
        :data-collapsed="isCollapsed ? '1' : '0'"
        @focusin="getServerInteraction(entryKey).focused = true"
        @focusout="onServerFocusOut(entryKey, $event)"
        @pointerdown="onServerPointerDown(entryKey, $event)"
        @pointerup="getServerInteraction(entryKey).touched = false"
        @pointercancel="getServerInteraction(entryKey).touched = false"
        @pointerleave="getServerInteraction(entryKey).touched = false"
      >
        <FloatingNotificationItemComponent
          :notificationItem="getServerStackEntry(entryKey).notificationItem"
          @open="openServerEntry(entryKey)"
          @close="dismissServerEntry(entryKey)"
          @read="markAsRead"
          @unread="markAsUnread"
          @hoverStart="setServerHovered(entryKey, true)"
          @hoverEnd="setServerHovered(entryKey, false)"
        />
      </div>
    </template>
  </FloatingMessageStackComponent>
</template>

<style scoped lang="scss">
  @use '@toolify/client/src/styles/variables' as variables;

  .floating-notifications-overlay {
    position: absolute;
    inset: 0;
    pointer-events: none;
    @include variables.zIndex(floatingNotifications);
  }

  // the server popup keeps its own look; the wrapper only takes part in the stack
  .stack-entry[data-kind="server"] {
    pointer-events: auto;

    &[data-collapsed="1"] {
      display: none;
    }

    &.floating-notification-enter-active,
    &.floating-notification-leave-active {
      transition: transform 0.22s ease, opacity 0.22s ease;
    }

    &.floating-notification-enter-from,
    &.floating-notification-leave-to {
      transform: translateX(100%);
      opacity: 0;
    }
  }

  .notification-pill {
    position: absolute;
    top: calc(#{variables.$contentSafeAreaTop} + 8px);
    right: 8px;
    pointer-events: auto;
    display: inline-flex;
    align-items: center;
    gap: map-get(variables.$spacing, space-1-5);
    max-width: min(72vw, 320px);
    padding: map-get(variables.$spacing, space-1) map-get(variables.$spacing, space-1-5) map-get(variables.$spacing, space-1) map-get(variables.$spacing, space-1);
    border-radius: map-get(variables.$radiuses, radius-full);
    background: map-get(variables.$colors, container-lowest);
    box-shadow: map-get(variables.$shadows, shadow-lg);
    color: map-get(variables.$colors, content-normal);
    animation: pill-in 0.22s ease;
  }

  .pill-main {
    display: inline-flex;
    flex: 1 1 auto;
    align-items: center;
    gap: map-get(variables.$spacing, space-1-5);
    min-width: 0;
    padding: 0;
    border: none;
    background: none;
    color: inherit;
    cursor: pointer;
  }

  .pill-avatar {
    display: flex;
    flex: 0 0 auto;
    width: 26px;
    height: 26px;

    :deep(.user-avatar) {
      width: 26px;
      height: 26px;
    }
  }

  .pill-label {
    font-size: map-get(variables.$textSizes, text-sm);
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .pill-close {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    padding: 0;
    border: none;
    border-radius: 50%;
    background: none;
    cursor: pointer;

    :deep(.icon) {
      color: map-get(variables.$colors, content-dim);
    }
  }

  @keyframes pill-in {
    from {
      transform: translateY(-14px);
      opacity: 0;
    }

    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
</style>
